// Final legal copy, approved by the legal team (v1.0, effective July 20, 2026).
// Do NOT edit the wording of the sections below without a new legal review and a
// version bump. If the version changes, bump AFFILIATE_TERMS_VERSION here so the
// rendered page and the value recorded by POST /affiliates/enroll stay in lockstep.
// House style: no em dashes in this file.
import React from 'react';

/** Recorded as `terms_version` by the enrollment endpoint. Must match the rendered version. */
export const AFFILIATE_TERMS_VERSION = '1.0';

/** Rendered alongside the version. Bump together with AFFILIATE_TERMS_VERSION. */
export const AFFILIATE_TERMS_EFFECTIVE_DATE = 'July 20, 2026';

/**
 * Shared Affiliate Program Terms & Conditions body.
 * Rendered both on the standalone /legal/affiliate-terms page and inside the
 * Settings affiliate enrollment modal. Styling comes from the parent context
 * (e.g. .legal-content on the standalone page).
 */
const AffiliateTermsContent = () => {
  return (
    <div className="affil-terms-content">
      <p>
        <strong>Version:</strong> {AFFILIATE_TERMS_VERSION}
        <br />
        <strong>Effective Date:</strong> {AFFILIATE_TERMS_EFFECTIVE_DATE}
      </p>

      <h2>1. Overview</h2>
      <p>
        These Affiliate / Partner Program Terms and Conditions (&quot;Affiliate Terms&quot;)
        govern your participation in the TradeMagic Affiliate / Partner Program (the
        &quot;Program&quot;), operated by Gifts From The Nine, LLC (&quot;TradeMagic,&quot;
        &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). These Affiliate Terms are in
        addition to the TradeMagic <a href="/terms">Terms of Service</a> and{' '}
        <a href="/privacy">Privacy Policy</a>, which continue to apply. By enrolling in the
        Program, you agree to these Affiliate Terms.
      </p>
      <p>
        The Program lets eligible users earn cash commissions for referring new subscribers to
        TradeMagic Elite. Participation is optional.
      </p>
      <p>
        Affiliate commissions are paid in cash and are separate from the TradeMagic points
        system. Commissions are not points, are not redeemable as points, and have no
        connection to the points economy.
      </p>

      <h2>2. Eligibility and Enrollment</h2>
      <p>
        To participate, you must have a TradeMagic account in good standing, enroll in the
        Program, accept these Affiliate Terms, and provide a valid PayPal account to receive
        payouts. You are responsible for the accuracy of your payout information and for
        keeping it current.
      </p>

      <h2>3. How You Earn a Commission</h2>
      <p>
        You earn a commission of five U.S. dollars ($5.00) for each verified new Elite
        subscription attributed to your referral. The commission is the same $5.00 whether the
        referred user subscribes to a monthly or an annual Elite plan.
      </p>
      <p>
        A commission is earned only for a referred user&apos;s first Elite subscription. If a
        referred user cancels and later subscribes again, that later subscription does not
        generate an additional commission. Each referred user can generate at most one
        commission for you, ever.
      </p>
      <p>
        A referral is attributed through your referral link. Commissions are created when a
        referred user completes their first qualifying Elite subscription, subject to the
        verification and void provisions below.
      </p>

      <h2>4. Referral Links and Username Changes</h2>
      <p>
        Your referral link is based on your username (for example,{' '}
        {'/elite/{your_username}'}). If you change your username, your previous referral link
        stops working. You are responsible for updating any referral links you have shared.
        TradeMagic is not responsible for lost attribution resulting from an outdated referral
        link after a username change.
      </p>

      <h2>5. Verification and Voiding of Earnings</h2>
      <p>
        Commissions are not final when created. A commission is voided if the referred
        subscription is refunded or disputed at any time before that commission has been paid
        out. When a referred subscription is refunded or disputed before payout, the associated
        unpaid commission is automatically voided and will not be paid.
      </p>
      <p>
        Once a commission has been paid out, the refund-void provision in this Section 5 no
        longer applies to that commission; however, the fraud and abuse provisions in Section 7
        continue to apply.
      </p>

      <h2>6. Payouts</h2>
      <p>
        Eligible commissions are paid monthly via PayPal to the email address on file for your
        Program account, regardless of the total amount accrued.
      </p>
      <p>
        You are responsible for maintaining a valid, active PayPal account and a current payout
        email address. We are not responsible for commissions that cannot be delivered because
        of inaccurate payout information, a closed or restricted PayPal account, or restrictions
        imposed by PayPal. Undeliverable commissions may be forfeited after a reasonable period.
      </p>

      <h2>7. Prohibited Conduct; Fraud and Abuse</h2>
      <p>
        Self-referrals are prohibited and are automatically blocked by the system. You may not
        refer yourself or accounts you own or control.
      </p>
      <p>
        You also may not create accounts to generate commissions, use deceptive or misleading
        promotion, misrepresent your relationship to TradeMagic, or otherwise abuse the Program.
      </p>
      <p>
        We may withhold payouts where we suspect fraud or abuse. If we withhold a payout on this
        basis, we will notify you by email. We may also void unpaid commissions and remove you
        from the Program if we determine, in our discretion, that referrals were fraudulent,
        invalid, or obtained in violation of these Affiliate Terms or the Terms of Service.
      </p>

      <h2>8. Taxes</h2>
      <p>
        You are solely responsible for reporting and paying any taxes on commissions you
        receive. In this version of the Program, TradeMagic does not issue tax forms such as
        Form 1099. TradeMagic tracks Program earnings, and tax reporting may be added in the
        future if required by law.
      </p>

      <h2>9. Suspension, Banning, and Forfeiture</h2>
      <p>
        If your TradeMagic account is banned or suspended, you forfeit all pending and
        otherwise-eligible Program earnings.
      </p>

      <h2>10. Changes to or Termination of the Program</h2>
      <p>
        We may modify or terminate the Program, in whole or in part, at any time. We will
        provide 30 days notice of material changes or termination by email. Commissions already
        earned and eligible for payout at the time of a change will be handled under the rules
        in effect when they were earned, except where voided or forfeited under these Affiliate
        Terms.
      </p>

      <h2>11. Relationship of the Parties</h2>
      <p>
        Participation in the Program does not create any employment, agency, partnership, or
        joint-venture relationship between you and TradeMagic. You participate as an independent
        party.
      </p>

      <h2>12. General</h2>
      <p>
        These Affiliate Terms are governed by and incorporated into the TradeMagic{' '}
        <a href="/terms">Terms of Service</a>, including its provisions on dispute resolution,
        arbitration, limitation of liability, and governing law. In the event of a conflict
        between these Affiliate Terms and the Terms of Service regarding the Program
        specifically, these Affiliate Terms control.
      </p>
    </div>
  );
};

export default AffiliateTermsContent;
